import { logger } from '@/lib/logger';

type TabStorageOptions<T> = {
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
};

/**
 * sessionStorage that survives it being unavailable.
 *
 * Private mode and blocked-cookie settings make every call throw, so each is guarded and the
 * value is mirrored in memory — the current page keeps working and only a refresh degrades.
 * Reads prefer the mirror, so a failed write is still visible to the tab that made it.
 */
export const createTabStorage = <T>(key: string, { serialize, deserialize }: TabStorageOptions<T> = {}) => {
  const encode = serialize ?? ((value: T) => JSON.stringify(value));
  const decode = deserialize ?? ((raw: string) => JSON.parse(raw) as T);

  let inMemory: T | null = null;

  const read = (): T | null => {
    if (inMemory !== null) return inMemory;
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw === null ? null : decode(raw);
    } catch {
      return null;
    }
  };

  const write = (value: T): void => {
    inMemory = value;
    try {
      window.sessionStorage.setItem(key, encode(value));
    } catch (error) {
      logger.warn('tab storage write failed', {
        storageKey: key,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const clear = (): void => {
    inMemory = null;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // The mirror is already null, but reads fall through to storage — so a failed remove
      // resurrects the value on the next read. Callers that clear for security reasons must
      // treat that as a failed clear, not a silent one.
    }
  };

  return { read, write, clear };
};
